import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, type WhatsAppAccount, type PhoneNumber, type CreateWhatsAppRequest, type CreatePhoneNumberRequest, type StoreWhatsAppRequest, type StoreFacebookRequest } from '../services/api';

// Query Keys
export const queryKeys = {
    whatsappAccounts: ['whatsapp-accounts'] as const,
    whatsappAccount: (id: string) => ['whatsapp-account', id] as const,
    phoneNumbers: ['phone-numbers'] as const,
    health: ['health'] as const,
};

// WhatsApp Accounts Hooks
export function useWhatsAppAccounts() {
    return useQuery({
        queryKey: queryKeys.whatsappAccounts,
        queryFn: () => apiService.getWhatsAppAccounts(),
    });
}

export function useWhatsAppAccount(id: string) {
    return useQuery({
        queryKey: queryKeys.whatsappAccount(id),
        queryFn: () => apiService.getWhatsAppAccount(id),
        enabled: !!id,
    });
}

export function useCreateWhatsAppAccount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateWhatsAppRequest) => apiService.createWhatsAppAccount(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.whatsappAccounts });
        },
    });
}

export function useUpdateWhatsAppAccount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<CreateWhatsAppRequest> }) =>
            apiService.updateWhatsAppAccount(id, data),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.whatsappAccounts });
            queryClient.invalidateQueries({ queryKey: queryKeys.whatsappAccount(id) });
        },
    });
}

export function useDeleteWhatsAppAccount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => apiService.deleteWhatsAppAccount(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.whatsappAccounts });
        },
    });
}

// Facebook Integration Hooks
export function useGenerateAuthUrl() {
    return useMutation({
        mutationFn: () => apiService.generateAuthUrl(),
    });
}

export function useStoreWhatsAppAccount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: StoreWhatsAppRequest) => apiService.storeWhatsAppAccount(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.whatsappAccounts });
            queryClient.invalidateQueries({ queryKey: queryKeys.phoneNumbers });
        },
    });
}

export function useStoreFacebookAccount() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: StoreFacebookRequest) => apiService.storeFacebookAccount(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.whatsappAccounts });
        },
    });
}

// Phone Numbers Hooks
export function usePhoneNumbers() {
    return useQuery({
        queryKey: queryKeys.phoneNumbers,
        queryFn: () => apiService.getPhoneNumbers(),
    });
}

export function useCreatePhoneNumbers() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreatePhoneNumberRequest) => apiService.createPhoneNumbers(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.phoneNumbers });
        },
    });
}

// Register and Subscribe Hooks
export function useRegisterPhoneNumber() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, pin }: { id: string; pin: string }) => apiService.registerPhoneNumber(id, pin),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.whatsappAccounts });
            queryClient.invalidateQueries({ queryKey: queryKeys.phoneNumbers });
        },
    });
}

export function useSubscribeApp() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => apiService.subscribeApp(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.whatsappAccounts });
            queryClient.invalidateQueries({ queryKey: queryKeys.phoneNumbers });
        },
    });
}

export function useUpdateWebhook() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, webhookUrl }: { id: string; webhookUrl: string }) => apiService.updateWebhook(id, webhookUrl),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.whatsappAccounts });
            queryClient.invalidateQueries({ queryKey: queryKeys.whatsappAccount(id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.phoneNumbers });
        },
    });
}

// Health Check Hook
export function useHealthCheck() {
    return useQuery({
        queryKey: queryKeys.health,
        queryFn: () => apiService.healthCheck(),
        refetchInterval: 30000, // Check every 30 seconds
    });
}
